import React, { useState } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  PauseCircle, 
  Edit3, 
  Save, 
  X, 
  AlertTriangle,
  Clock
} from 'lucide-react';
import apiService from '../../services/api';

const ContactStatus = ({ contact, onContactUpdate, onError, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(contact.status || 'ativo');
  const [blockReason, setBlockReason] = useState('');
  const [saving, setSaving] = useState(false);

  const statusConfig = {
    ativo: {
      icon: CheckCircle,
      label: 'Ativo',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      description: 'Contato pode receber mensagens'
    },
    ignorado: {
      icon: XCircle,
      label: 'Ignorado',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
      description: 'Contato não receberá mensagens futuras'
    },
    pausado: {
      icon: PauseCircle,
      label: 'Pausado',
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      description: 'Contato temporariamente pausado'
    }
  };

  const handleSaveStatus = async () => {
    try {
      setSaving(true);

      const payload = {
        status: selectedStatus
      };

      // Se status for "ignorado" e há uma razão, incluir
      if (selectedStatus === 'ignorado' && blockReason.trim()) {
        payload.reason = blockReason.trim();
      }

      const response = await apiService.request(`/crm/contacts/${contact.id}/status`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.success && response.data.contact) {
        onContactUpdate(response.data.contact);
        // Notificação removida
        setIsEditing(false);
        setBlockReason('');
      } else {
        throw new Error(response.message || 'Erro ao atualizar status');
      }

    } catch (error) {
      onError('Erro ao salvar status: ' + error.message);
      // Reverter mudanças locais
      setSelectedStatus(contact.status || 'ativo');
      setBlockReason('');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedStatus(contact.status || 'ativo');
    setBlockReason('');
    setIsEditing(false);
  };

  const currentStatus = contact.status || 'ativo';
  const currentConfig = statusConfig[currentStatus] || statusConfig.ativo;
  const CurrentIcon = currentConfig?.icon || CheckCircle;

  const formatBlockedDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
          <CurrentIcon className={`w-4 h-4 mr-2 ${currentConfig?.color || 'text-gray-400'}`} />
          Status do Contato
        </h4>
        
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Modo de visualização */}
      {!isEditing && (
        <div className="space-y-2">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${currentConfig?.bgColor || 'bg-gray-100'} ${currentConfig?.color || 'text-gray-400'}`}>
            <CurrentIcon className="w-4 h-4 mr-2" />
            {currentConfig?.label || 'Status desconhecido'}
          </div>
          
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {currentConfig?.description || 'Status não definido'}
          </div>

          {/* Informações de bloqueio */}
          {currentStatus === 'ignorado' && (contact.blocked_at || contact.blocked_reason) && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="text-red-800 dark:text-red-200">
                  <div className="font-medium mb-1">Contato bloqueado</div>
                  {contact.blocked_at && (
                    <div className="flex items-center text-xs mb-1">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatBlockedDate(contact.blocked_at)}
                    </div>
                  )}
                  {contact.blocked_reason && (
                    <div className="text-xs">
                      <span className="font-medium">Motivo:</span> {contact.blocked_reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modo de edição */}
      {isEditing && (
        <div className="space-y-3">
          {/* Seletor de status */}
          <div className="space-y-2">
            {Object.entries(statusConfig).map(([status, config]) => {
              const Icon = config.icon;
              const isSelected = selectedStatus === status;
              
              return (
                <label
                  key={status}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={status}
                    checked={isSelected}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="sr-only"
                  />
                  
                  <div className="flex items-center space-x-3 flex-1">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <div>
                      <div className={`font-medium ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`}>
                        {config.label}
                      </div>
                      <div className={`text-sm ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}`}>
                        {config.description}
                      </div>
                    </div>
                  </div>
                  
                  {isSelected && (
                    <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          {/* Campo de motivo para status "ignorado" */}
          {selectedStatus === 'ignorado' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo do bloqueio (opcional)
              </label>
              <input
                type="text"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Ex: Solicitou para não receber mensagens"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveStatus}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-3 h-3 mr-2" />
                  Salvar
                </>
              )}
            </button>
            
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
            >
              <X className="w-3 h-3 mr-2" />
              Cancelar
            </button>
          </div>

          {/* Aviso sobre mudanças */}
          {selectedStatus !== currentStatus && (
            <div className="flex items-start space-x-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
              <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-yellow-800 dark:text-yellow-200">
                <div className="font-medium">Status será alterado:</div>
                <div className="text-xs mt-1">
                  De "{statusConfig[currentStatus]?.label || 'Status desconhecido'}" para "{statusConfig[selectedStatus]?.label || 'Status desconhecido'}"
                </div>
                {selectedStatus === 'ignorado' && (
                  <div className="text-xs mt-1 font-medium text-red-700 dark:text-red-300">
                    ⚠️ Contato não receberá mensagens futuras
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactStatus;